<template>
  <div>
    <PreviewBanner v-if="$store.getters.preview">
      <span v-if="$store.getters.mode === 'test' || $store.getters.mode === 'sandbox'">You're viewing a test {{ isPaymentIntentBased ? 'payment' : 'invoice' }}.</span>
      <span v-else>This is a live {{ isPaymentIntentBased ? 'payment' : 'invoice' }}, so please proceed with care.</span>
    </PreviewBanner>
    <div class="flex flex-col md:flex-row">
      <!-- left section -->
      <div class="w-screen justify-center md:content-center md:h-screen md:w-1/2 sm:content-start bg-white flex flex-wrap">
        <div class="mx-auto w-full max-w-screen-sm md:w-full sm:w-3/4 lg:p-14 md:p-8 p-6 bg-white">
          <div
            v-if="existingDiscount && !paymentComplete"
            class="p-4 mb-8 text-sm text-blue-700 bg-blue-100 rounded-lg dark:bg-blue-200 dark:text-blue-800"
            role="alert"
          >
            <i18n path="discount.note">
              <template #discount><b>{{ existingDiscountLabel }}</b></template>
              <template #validUntil>{{ existingDiscountValidUntil }}</template>
            </i18n>
          </div>
          <div
            v-if="existingDiscount && paymentComplete"
            class="p-4 mb-8 text-sm text-blue-700 bg-blue-100 rounded-lg dark:bg-blue-200 dark:text-blue-800"
            role="alert"
          >
            <i18n path="discount.active">
              <template #discount><b>{{ existingDiscountLabel }}</b></template>
              <template #validUntil>{{ existingDiscountValidUntil }}</template>
            </i18n>
          </div>
          <BrandImage :src="companyLogo" alignment="left" v-if="companyLogo" class="mb-4 mt-4 md:mt-0" />
          <h1 v-else-if="companyName" class="text-xl text-center md:text-left text-brand-black font-semibold mb-6">{{ companyName }}</h1>
          <i18n :path="promptKey" tag="p" class="text-base md:text-xl text-brand-medium-brown leading-relaxed md:mb-8 text-center md:text-left">
            <template #company><a :href="redirectLink" target="_blank" class="cursor-pointer underline underline-offset-8">{{ companyName }}</a></template>
          </i18n>
          <div class="space-y-2 divide-y divide-brand-sand">
            <p v-if="customerPlan" class="text-sm text-brand-black mb-2 font-medium text-center md:text-left md:block hidden">
              {{ $t('invoice.planOf', { name: customerName, plan: customerPlan }) }}
            </p>
            <!-- Payment Intent Details (for PI-based dunning) -->
            <div v-if="isPaymentIntentBased && dunningPaymentIntent" class="hidden md:block">
              <div class="flex items-center justify-between py-4">
                <div class="flex align-items-center">
                  <div class="">
                    <span class="text-brand-black font-semibold">{{ dunningPaymentIntent.amount | formatAmount(dunningPaymentIntent.currency) }}</span>
                    <p v-if="dunningPaymentIntent.description" class="text-xs text-brand-medium-brown font-medium">
                      {{ dunningPaymentIntent.description }}
                    </p>
                  </div>
                </div>
                <span v-if="alreadyPaid" class="bg-green-100 rounded-3xl font-semibold text-sm px-4 py-1 text-green-900">{{ $t('status.paid') }}</span>
                <span v-else-if="isPendingCapture" class="bg-green-100 rounded-3xl font-semibold text-sm px-4 py-1 text-green-900">{{ $t('status.pending') }}</span>
                <span v-else-if="isPendingVerification" class="bg-yellow-100 rounded-3xl font-semibold text-sm px-4 py-1 text-yellow-900">{{ $t('status.pendingVerification') }}</span>
                <span v-else-if="isCanceled" class="bg-red-100 rounded-3xl font-semibold text-sm px-4 py-1 text-red-900">{{ $t('status.canceled') }}</span>
                <span v-else-if="isProcessing" class="bg-blue-100 rounded-3xl font-semibold text-sm px-4 py-1 text-blue-900">{{ $t('status.processing') }}</span>
                <span v-else-if="isRequiresConfirmation" class="bg-yellow-100 rounded-3xl font-semibold text-sm px-4 py-1 text-yellow-900">{{ $t('status.pending') }}</span>
                <span v-else class="bg-brand-sand rounded-3xl font-semibold text-sm px-4 py-1 text-brand-brown">{{ $t('status.failed') }}</span>
              </div>
            </div>
            <!-- Desktop Invoice Items -->
            <div v-else class="hidden md:block">
              <div class="flex items-center justify-between py-4" v-for="bill in invoices" :key="bill.invoice">
                <div class="flex align-items-center">
                  <div v-if="bill.displayDiscount" class="bg-red w-16 bg-orange-100 leading-10 text-center align-middle rounded-md text-gray-900 mr-2">
                    <span>-{{ bill.displayDiscount.coupon | formatCouponAmount }}</span>
                  </div>
                  <div class="">
                    <span>
                      <span v-if="bill.displayDiscount" class="text-brand-medium-brown font-medium mr-1 line-through">{{
                        bill | amountBeforeDiscount | formatAmount(bill.currency)
                      }}</span>
                      <span class="text-brand-black font-semibold">{{ bill | amountAfterDiscount | formatAmount(bill.currency) }}</span>
                    </span>
                    <p class="text-xs text-brand-medium-brown font-medium">
                      <!-- Future invoice preview with discount -->
                      <span v-if="bill.status === 'draft' && bill.displayDiscount">
                        <!-- Forever future discount -->
                        <i18n v-if="bill.displayDiscount.coupon.couponDuration === null" path="discount.startingForever">
                          <template #date>{{ bill.periodEnd | formatDate }}</template>
                        </i18n>
                        <!-- Temporary future discount -->
                        <span v-else>
                          {{ bill.periodEnd | formatDate }}
                          {{ bill.displayDiscount.coupon | translateCouponDuration('times') }}
                        </span>
                      </span>
                      <span v-else>
                        <!-- Regular date display -->
                        {{ bill.created | formatDate }}
                      </span>
                    </p>
                  </div>
                </div>
                <span v-if="bill.status === 'paid'" class="bg-green-100 rounded-3xl font-semibold text-sm px-4 py-1 text-green-900">{{ $t('status.paid') }}</span>
                <span v-else-if="bill.status === 'uncollectible'" class="bg-red-100 rounded-3xl font-semibold text-sm px-4 py-1 text-red-900">{{ $t('status.pastDue') }}</span>
                <span v-else-if="bill.upcoming || (bill.status === 'draft' && !bill.invoice)" class="bg-fuchsia-200 rounded-3xl font-semibold text-sm px-4 py-1 text-fuchsia-900"
                  >{{ $t('status.futureOffer') }}</span
                >
                <span
                  v-else-if="bill.paymentIntent && bill.paymentIntent.status === 'requires_action'"
                  class="bg-yellow-100 rounded-3xl font-semibold text-sm px-4 py-1 text-yellow-900"
                  >{{ $t('status.pendingVerification') }}</span
                >
                <span v-else class="bg-brand-sand rounded-3xl font-semibold text-sm px-4 py-1 text-brand-brown">{{ $t('status.failed') }}</span>
              </div>
            </div>
          </div>
          <div v-if="showWatermark" class="mt-6 flex justify-center md:justify-start">
            <Watermark :message="$t('watermark.verified')" product="pr" align="left" />
          </div>
        </div>
      </div>
      <!-- right section -->
      <div class="md:w-1/2 bg-brand-light-sand bg-neutral-800 justify-center md:content-center xs:content-start flex flex-wrap pt-6 md:pt-0">
        <StripeForm class="px-0 pt-0 bg-brand-light-sand" />

        <!-- Mobile Accordion Toggle -->
        <div class="block md:hidden mx-auto w-full max-w-screen-sm sm:w-3/4 p-6">
          <div class="flex items-center justify-between pb-4 cursor-pointer" @click="toggleInvoiceDetails">
            <span class="text-sm font-medium text-brand-black">{{ isPaymentIntentBased ? $t('invoice.seePaymentDetails') : $t('invoice.seeInvoiceHistory') }}</span>
            <svg
              class="w-5 h-5 transform transition-transform duration-200"
              :class="{ 'rotate-180': showInvoiceDetails }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <!-- Mobile Payment Intent Details (for PI-based dunning) -->
          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform opacity-0 ease-out"
            enter-to-class="transform opacity-100 ease-out"
            leave-active-class="transition duration-200 ease-out"
            leave-from-class="transform opacity-100 ease-out"
            leave-to-class="transform opacity-0 ease-out"
          >
            <div v-if="showInvoiceDetails && isPaymentIntentBased && dunningPaymentIntent" class="block md:hidden">
              <div class="flex items-center justify-between py-4">
                <div class="flex align-items-center">
                  <div class="">
                    <span class="text-brand-black font-semibold">{{ dunningPaymentIntent.amount | formatAmount(dunningPaymentIntent.currency) }}</span>
                    <p v-if="dunningPaymentIntent.description" class="text-xs text-brand-medium-brown font-medium">
                      {{ dunningPaymentIntent.description }}
                    </p>
                  </div>
                </div>
                <span v-if="alreadyPaid" class="bg-green-100 rounded-3xl font-semibold text-sm px-4 py-1 text-green-900">{{ $t('status.paid') }}</span>
                <span v-else-if="isPendingCapture" class="bg-green-100 rounded-3xl font-semibold text-sm px-4 py-1 text-green-900">{{ $t('status.pending') }}</span>
                <span v-else-if="isPendingVerification" class="bg-yellow-100 rounded-3xl font-semibold text-sm px-4 py-1 text-yellow-900">{{ $t('status.pendingVerification') }}</span>
                <span v-else-if="isCanceled" class="bg-red-100 rounded-3xl font-semibold text-sm px-4 py-1 text-red-900">{{ $t('status.canceled') }}</span>
                <span v-else-if="isProcessing" class="bg-blue-100 rounded-3xl font-semibold text-sm px-4 py-1 text-blue-900">{{ $t('status.processing') }}</span>
                <span v-else-if="isRequiresConfirmation" class="bg-yellow-100 rounded-3xl font-semibold text-sm px-4 py-1 text-yellow-900">{{ $t('status.pending') }}</span>
                <span v-else class="bg-brand-sand rounded-3xl font-semibold text-sm px-4 py-1 text-brand-brown">{{ $t('status.failed') }}</span>
              </div>
            </div>
          </transition>
          <!-- Mobile Invoice Items (Accordion) -->
          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform opacity-0 ease-out"
            enter-to-class="transform opacity-100 ease-out"
            leave-active-class="transition duration-200 ease-out"
            leave-from-class="transform opacity-100 ease-out"
            leave-to-class="transform opacity-0 ease-out"
          >
            <div v-if="showInvoiceDetails && !isPaymentIntentBased" class="block md:hidden">
              <div class="flex items-center justify-between py-4" v-for="bill in invoices" :key="bill.invoice">
                <div class="flex align-items-center">
                  <div v-if="bill.displayDiscount" class="bg-red w-16 bg-orange-100 leading-10 text-center align-middle rounded-md text-gray-900 mr-2">
                    <span>-{{ bill.displayDiscount.coupon | formatCouponAmount }}</span>
                  </div>
                  <div class="">
                    <span>
                      <span v-if="bill.displayDiscount" class="text-brand-medium-brown font-medium mr-1 line-through">{{
                        bill | amountBeforeDiscount | formatAmount(bill.currency)
                      }}</span>
                      <span class="text-brand-black font-semibold">{{ bill | amountAfterDiscount | formatAmount(bill.currency) }}</span>
                    </span>
                    <p class="text-xs text-brand-medium-brown font-medium">
                      <!-- Future invoice preview with discount -->
                      <span v-if="bill.status === 'draft' && bill.displayDiscount">
                        <!-- Forever future discount -->
                        <i18n v-if="bill.displayDiscount.coupon.couponDuration === null" path="discount.startingForever">
                          <template #date>{{ bill.periodEnd | formatDate }}</template>
                        </i18n>
                        <!-- Temporary future discount -->
                        <span v-else>
                          {{ bill.periodEnd | formatDate }}
                          {{ bill.displayDiscount.coupon | translateCouponDuration('times') }}
                        </span>
                      </span>
                      <span v-else>
                        <!-- Regular date display -->
                        {{ bill.created | formatDate }}
                      </span>
                    </p>
                  </div>
                </div>
                <span v-if="bill.status === 'paid'" class="bg-green-100 rounded-3xl font-semibold text-sm px-4 py-1 text-green-900">{{ $t('status.paid') }}</span>
                <span v-else-if="bill.status === 'uncollectible'" class="bg-red-100 rounded-3xl font-semibold text-sm px-4 py-1 text-red-900">{{ $t('status.pastDue') }}</span>
                <span v-else-if="bill.upcoming || (bill.status === 'draft' && !bill.invoice)" class="bg-fuchsia-200 rounded-3xl font-semibold text-sm px-4 py-1 text-fuchsia-900"
                  >{{ $t('status.futureOffer') }}</span
                >
                <span
                  v-else-if="bill.paymentIntent && bill.paymentIntent.status === 'requires_action'"
                  class="bg-yellow-100 rounded-3xl font-semibold text-sm px-4 py-1 text-yellow-900"
                  >{{ $t('status.pendingVerification') }}</span
                >
                <span v-else class="bg-brand-sand rounded-3xl font-semibold text-sm px-4 py-1 text-brand-brown">{{ $t('status.failed') }}</span>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EventBus from '@/helpers/event-bus';
import * as Filters from '@/helpers/filters';
import StripeForm from '@/components/StripeForm.vue';
import Watermark from '@/components/Watermark.vue';
import { getIntegerCurrencyString } from '@/helpers/currency-utils';
import { initDwellTracking } from '@/helpers/page-events';
import { getInvoiceDescription } from '@/helpers/subscription-utils';
import PreviewBanner from '@/components/PreviewBanner.vue';
import BrandImage from '@/components/BrandImage.vue';
import i18n from '@/i18n';

const formatDate = (date) => {
  if (date == null) return '';
  return new Intl.DateTimeFormat(i18n.locale, { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(date));
};

export default {
  name: 'home',
  components: {
    StripeForm,
    Watermark,
    PreviewBanner,
    BrandImage,
  },
  data() {
    return {
      showInvoiceDetails: false,
    };
  },
  mounted() {
    EventBus.$on('alert', (title, msg) => this.launchAlert(title, msg));
    EventBus.$on('invoicePaid', (invoice) => this.markInvoicePaid(invoice));
    initDwellTracking();
  },
  computed: {
    account() {
      return this.$store.state?.account;
    },
    customerName() {
      return this.account?.name || this.account?.email;
    },
    invoices() {
      // map these to handle payment status and amount due
      return this.account?.invoices;
    },
    isPaymentIntentBased() {
      return this.account?.isPaymentIntentBased === true;
    },
    dunningPaymentIntent() {
      return this.account?.dunning?.paymentIntent;
    },
    dunningInvoice() {
      // For PaymentIntent-based dunning, there is no invoice
      if (this.isPaymentIntentBased) {
        return null;
      }
      const dunningInvoiceId = this.account.dunning?.invoice?.invoiceId;
      if (!dunningInvoiceId) {
        return this.account.dunning?.invoice;
      }
      const dunningInvoice = this.invoices?.find((invoice) => invoice.invoice === dunningInvoiceId);
      return dunningInvoice || this.account.dunning.invoice;
    },
    alreadyPaid() {
      if (this.isPaymentIntentBased) {
        return this.dunningPaymentIntent?.status === 'succeeded';
      }
      return this.dunningInvoice?.status === 'paid';
    },
    isPendingCapture() {
      // Manual capture: payment method confirmed, funds authorized, platform captures later
      if (this.isPaymentIntentBased) {
        return this.dunningPaymentIntent?.status === 'requires_capture';
      }
      return false;
    },
    isPendingVerification() {
      if (this.isPaymentIntentBased) {
        return this.dunningPaymentIntent?.status === 'requires_action';
      }
      return this.dunningInvoice?.paymentIntent?.status === 'requires_action';
    },
    isCanceled() {
      if (this.isPaymentIntentBased) {
        return this.dunningPaymentIntent?.status === 'canceled';
      }
      return false;
    },
    isProcessing() {
      if (this.isPaymentIntentBased) {
        return this.dunningPaymentIntent?.status === 'processing';
      }
      return false;
    },
    isRequiresConfirmation() {
      if (this.isPaymentIntentBased) {
        return this.dunningPaymentIntent?.status === 'requires_confirmation';
      }
      return false;
    },
    paymentComplete() {
      // True when the customer has nothing left to do (hide the form)
      return this.alreadyPaid || this.isPendingCapture;
    },
    existingDiscount() {
      return this.account?.existingDiscount;
    },
    existingDiscountLabel() {
      const { coupon } = this.existingDiscount;
      return this.$t('discount.amountOff', { amount: Filters.formatCouponAmount(coupon), duration: Filters.translateCouponDuration(coupon, 'for') });
    },
    existingDiscountValidUntil() {
      return this.existingDiscount.end ? this.$t('discount.validUntil', { date: formatDate(this.existingDiscount.end) }) : '';
    },
    promptKey() {
      if (this.isPendingVerification) return 'prompt.verify';
      return this.$store.getters.isNonProfit ? 'prompt.updateNonProfit' : 'prompt.update';
    },
    customerPlan() {
      // For PaymentIntent-based dunning, use the payment intent description
      if (this.isPaymentIntentBased) {
        return this.dunningPaymentIntent?.description || this.$t('invoice.paymentFallback');
      }
      if (this.invoices?.length) {
        return getInvoiceDescription(this.invoices[0]);
      }
      return null;
    },
    futureDiscount() {
      if (!this.account.offers || this.account.offers.length === 0) return null;
      return this.account.offers.find((o) => o.offerType === 'DISCOUNT');
    },
    invoiceDiscount() {
      if (!this.account.offers || this.account.offers.length === 0) return null;
      return this.account.offers.find((o) => o.offerType === 'INVOICE_DISCOUNT');
    },
    companyName() {
      return this.account?.orgName;
    },
    companyLogo() {
      return this.account.branding?.brandImage;
    },
    redirectLink() {
      let link = this.account?.dunning?.checkoutPage?.redirectLink;
      // prepend https:// if not already there
      if (link && link.indexOf('://') === -1) {
        link = `https://${link}`;
      }
      return link;
    },
    showWatermark() {
      return this.account?.branding?.showWatermark;
    },
    isLoggedIn() {
      return this.$store.getters.isLoggedIn;
    },
    errorContext() {
      return this.$store.state.account;
    },
  },
  filters: {
    formatDate,
    formatAmount(amount, currency) {
      return getIntegerCurrencyString(amount, currency, false);
      // return `$${(amount / 100).toFixed(2)}`;
    },
    amountBeforeDiscount(invoice) {
      if (invoice.displayDiscount !== undefined) {
        return invoice.displayDiscount.amountBeforeDiscount;
      }
      return '';
    },
    amountAfterDiscount(invoice) {
      if (invoice.displayDiscount !== undefined) {
        return invoice.displayDiscount.amountAfterDiscount;
      }
      if (invoice.status === 'paid') return invoice.amountPaid;
      return invoice.amountRemaining;
    },
    translateCouponDuration: Filters.translateCouponDuration,
    formatCouponAmount: Filters.formatCouponAmount,
  },
  methods: {
    markInvoicePaid(paymentResult) {
      if (this.isPaymentIntentBased) {
        // PaymentIntent-based dunning - update the PaymentIntent status
        this.$store.commit('UPDATE_PAYMENT_STATUS', {
          type: 'paymentIntent',
          status: paymentResult.status,
        });
      } else {
        // Invoice-based dunning - update the invoice status
        this.$store.commit('UPDATE_PAYMENT_STATUS', {
          type: 'invoice',
          invoiceId: paymentResult.invoice,
          status: paymentResult.status,
          amountPaid: paymentResult.amountPaid,
        });
      }
    },
    toggleInvoiceDetails() {
      this.showInvoiceDetails = !this.showInvoiceDetails;
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/style.scss';
</style>
